import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Camera, X, Maximize2, RotateCcw, Download, Smartphone, AlertCircle, Wand2, Loader2, Palette, ChevronUp, ChevronDown, Move, GripHorizontal } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Predefined shade colors
const SHADE_COLORS = [
  { id: 'white', name: 'White', hex: '#ffffff' },
  { id: 'ivory', name: 'Ivory', hex: '#f5f5dc' },
  { id: 'cream', name: 'Cream', hex: '#fffdd0' },
  { id: 'beige', name: 'Beige', hex: '#d4c4a8' },
  { id: 'gray', name: 'Gray', hex: '#9ca3af' },
  { id: 'charcoal', name: 'Charcoal', hex: '#4b5563' },
  { id: 'black', name: 'Black', hex: '#1f2937' },
  { id: 'navy', name: 'Navy', hex: '#1e3a5f' },
  { id: 'brown', name: 'Brown', hex: '#8b7355' },
  { id: 'taupe', name: 'Taupe', hex: '#b8a88a' },
];

const ARViewer = ({ config, productType, selectedColor: initialColor, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [shadePosition, setShadePosition] = useState({ x: 50, y: 30 });
  const [shadeSize, setShadeSize] = useState({ width: 200, height: 300 });
  const [shadeOpenPercent, setShadeOpenPercent] = useState(0); // 0 = fully closed, 100 = fully open
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isAdjustingShade, setIsAdjustingShade] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [capturedImage, setCapturedImage] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [detectedWindows, setDetectedWindows] = useState([]);
  const [selectedWindow, setSelectedWindow] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [currentColor, setCurrentColor] = useState(initialColor || SHADE_COLORS[0]);
  const [shadeAdjustStart, setShadeAdjustStart] = useState(0);
  const [videoDisplayMetrics, setVideoDisplayMetrics] = useState(null);

  // Calculate video display metrics for object-cover
  const updateVideoDisplayMetrics = useCallback(() => {
    if (!containerRef.current || !videoRef.current) return null;
    
    const container = containerRef.current;
    const video = videoRef.current;
    const containerRect = container.getBoundingClientRect();
    
    const videoNativeWidth = video.videoWidth || 1920;
    const videoNativeHeight = video.videoHeight || 1080;
    
    const containerAspect = containerRect.width / containerRect.height;
    const videoAspect = videoNativeWidth / videoNativeHeight;
    
    let displayWidth, displayHeight, offsetX, offsetY;
    
    if (containerAspect > videoAspect) {
      displayWidth = containerRect.width;
      displayHeight = containerRect.width / videoAspect;
      offsetX = 0;
      offsetY = (containerRect.height - displayHeight) / 2;
    } else {
      displayHeight = containerRect.height;
      displayWidth = containerRect.height * videoAspect;
      offsetX = (containerRect.width - displayWidth) / 2;
      offsetY = 0;
    }
    
    const metrics = {
      displayWidth,
      displayHeight,
      offsetX,
      offsetY,
      containerWidth: containerRect.width,
      containerHeight: containerRect.height,
      videoNativeWidth,
      videoNativeHeight
    };
    
    setVideoDisplayMetrics(metrics);
    return metrics;
  }, []);

  // Convert percentage coordinates to pixel position for display
  const getWindowDisplayPosition = useCallback((win) => {
    const metrics = videoDisplayMetrics || updateVideoDisplayMetrics();
    if (!metrics) return { left: `${win.x}%`, top: `${win.y}%`, width: `${win.width}%`, height: `${win.height}%` };
    
    const left = metrics.offsetX + (win.x / 100) * metrics.displayWidth;
    const top = metrics.offsetY + (win.y / 100) * metrics.displayHeight;
    const width = (win.width / 100) * metrics.displayWidth;
    const height = (win.height / 100) * metrics.displayHeight;
    
    return { left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${height}px` };
  }, [videoDisplayMetrics, updateVideoDisplayMetrics]);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  // Update metrics when video loads
  useEffect(() => {
    if (isStreaming && videoRef.current) {
      const video = videoRef.current;
      const handleLoadedMetadata = () => {
        updateVideoDisplayMetrics();
      };
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      // Also update on resize
      window.addEventListener('resize', updateVideoDisplayMetrics);
      // Initial update after a short delay
      setTimeout(updateVideoDisplayMetrics, 100);
      
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        window.removeEventListener('resize', updateVideoDisplayMetrics);
      };
    }
  }, [isStreaming, updateVideoDisplayMetrics]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsStreaming(true);
        setError(null);
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Unable to access camera. Please allow camera permissions.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setIsStreaming(false);
  };

  // Get current position from touch or mouse event
  const getEventPosition = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  // Handle drag start
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getEventPosition(e);
    setDragStart({ x: pos.x - shadePosition.x, y: pos.y - shadePosition.y });
    setIsDragging(true);
  }, [shadePosition]);

  // Handle resize start
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  }, []);

  // Handle shade open/close adjustment start
  const handleShadeAdjustStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getEventPosition(e);
    setShadeAdjustStart(pos.y);
    setIsAdjustingShade(true);
  }, []);

  // Handle move (drag, resize, or shade adjustment)
  const handleMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pos = getEventPosition(e);
    
    if (isDragging) {
      const newX = Math.max(0, Math.min(rect.width - shadeSize.width, pos.x - dragStart.x));
      const newY = Math.max(0, Math.min(rect.height - shadeSize.height, pos.y - dragStart.y));
      setShadePosition({ x: newX, y: newY });
    }
    
    if (isResizing) {
      const newWidth = Math.max(80, Math.min(rect.width - shadePosition.x, pos.x - rect.left - shadePosition.x));
      const newHeight = Math.max(100, Math.min(rect.height - shadePosition.y, pos.y - rect.top - shadePosition.y));
      setShadeSize({ width: newWidth, height: newHeight });
    }

    if (isAdjustingShade) {
      const deltaY = shadeAdjustStart - pos.y;
      const maxDelta = shadeSize.height * 0.8; // Max 80% open
      const newPercent = Math.max(0, Math.min(80, shadeOpenPercent + (deltaY / maxDelta) * 100));
      setShadeOpenPercent(newPercent);
      setShadeAdjustStart(pos.y);
    }
  }, [isDragging, isResizing, isAdjustingShade, dragStart, shadePosition, shadeSize, shadeOpenPercent, shadeAdjustStart]);

  // Handle end
  const handleEnd = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setIsAdjustingShade(false);
  }, []);

  // Capture current frame for window detection
  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) return null;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const video = videoRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  // AI-powered window detection
  const detectWindows = async () => {
    if (!isStreaming) return;
    
    setDetecting(true);
    try {
      const imageData = captureFrame();
      if (!imageData) {
        throw new Error('Could not capture frame');
      }

      const response = await axios.post(`${API_URL}/ar/detect-windows`, {
        image: imageData
      });

      if (response.data.windows && response.data.windows.length > 0) {
        setDetectedWindows(response.data.windows);
        // Auto-select first window
        const firstWindow = response.data.windows[0];
        applyWindowDetection(firstWindow);
        toast.success(`Detected ${response.data.windows.length} window(s)! You can now adjust position manually.`);
      } else {
        toast.info('No windows detected. Try adjusting your camera angle.');
      }
    } catch (error) {
      console.error('Window detection error:', error);
      toast.error('Window detection unavailable. Position shade manually.');
    } finally {
      setDetecting(false);
    }
  };

  // Apply detected window position with edge-to-edge fit
  const applyWindowDetection = (window) => {
    const metrics = videoDisplayMetrics || updateVideoDisplayMetrics();
    if (!metrics) return;
    
    // The AI returns percentages relative to the full video frame
    // Convert to display coordinates (accounting for object-cover cropping)
    const x = metrics.offsetX + (window.x / 100) * metrics.displayWidth;
    const y = metrics.offsetY + (window.y / 100) * metrics.displayHeight;
    const width = (window.width / 100) * metrics.displayWidth;
    const height = (window.height / 100) * metrics.displayHeight;
    
    setShadePosition({ x, y });
    setShadeSize({ width, height });
    setSelectedWindow(window);
    setShadeOpenPercent(0); // Reset to fully closed
  };

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current || !containerRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const video = videoRef.current;
    const container = containerRef.current;
    
    // Get actual video dimensions
    const videoNativeWidth = video.videoWidth;
    const videoNativeHeight = video.videoHeight;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate how the video is displayed with object-cover
    const containerAspect = containerRect.width / containerRect.height;
    const videoAspect = videoNativeWidth / videoNativeHeight;
    
    let displayWidth, displayHeight, offsetX, offsetY;
    
    if (containerAspect > videoAspect) {
      // Container is wider - video is stretched to fill width, cropped top/bottom
      displayWidth = containerRect.width;
      displayHeight = containerRect.width / videoAspect;
      offsetX = 0;
      offsetY = (containerRect.height - displayHeight) / 2;
    } else {
      // Container is taller - video is stretched to fill height, cropped left/right
      displayHeight = containerRect.height;
      displayWidth = containerRect.height * videoAspect;
      offsetX = (containerRect.width - displayWidth) / 2;
      offsetY = 0;
    }
    
    // Set canvas to match video native resolution
    canvas.width = videoNativeWidth;
    canvas.height = videoNativeHeight;
    
    // Draw video frame
    ctx.drawImage(video, 0, 0);
    
    // Calculate scale factors from display to native video coordinates
    const scaleX = videoNativeWidth / displayWidth;
    const scaleY = videoNativeHeight / displayHeight;
    
    // Convert shade position from container coordinates to video coordinates
    // Account for object-cover offset
    const shadeX = (shadePosition.x - offsetX) * scaleX;
    const shadeY = (shadePosition.y - offsetY) * scaleY;
    const shadeW = shadeSize.width * scaleX;
    const shadeH = shadeSize.height * scaleY;
    const openOffset = (shadeOpenPercent / 100) * shadeH * 0.8;
    
    // Draw shade based on product type
    ctx.fillStyle = currentColor?.hex || '#e5e5e5';
    ctx.globalAlpha = getShadeOpacity();
    
    if (productType === 'roller' || productType === 'roller-shades') {
      // Top bar
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#444';
      ctx.fillRect(shadeX, shadeY, shadeW, 8);
      // Shade fabric (adjusted for open state)
      ctx.fillStyle = currentColor?.hex || '#e5e5e5';
      ctx.globalAlpha = getShadeOpacity();
      ctx.fillRect(shadeX, shadeY + 8, shadeW, shadeH - 18 - openOffset);
      // Bottom bar
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#555';
      ctx.fillRect(shadeX, shadeY + shadeH - 10 - openOffset, shadeW, 10);
    } else if (productType === 'drape' || productType === 'motorized-drapes') {
      const openWidth = (shadeOpenPercent / 100) * shadeW * 0.3;
      ctx.fillRect(shadeX, shadeY, shadeW * 0.32 - openWidth, shadeH);
      ctx.fillRect(shadeX + shadeW * 0.68 + openWidth, shadeY, shadeW * 0.32 - openWidth, shadeH);
    } else if (productType === 'roman' || productType === 'roman-shades') {
      const visibleHeight = (shadeH * 0.7) - openOffset;
      ctx.fillRect(shadeX, shadeY, shadeW, Math.max(20, visibleHeight));
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(0,0,0,0.15)';
      for (let i = 1; i <= 4; i++) {
        const foldY = shadeY + Math.max(20, visibleHeight) - (i * 12);
        if (foldY > shadeY) {
          ctx.fillRect(shadeX, foldY, shadeW, 2);
        }
      }
    }
    
    ctx.globalAlpha = 1;
    
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(imageData);
  };

  const handleDownload = () => {
    if (!capturedImage) return;
    const link = document.createElement('a');
    link.download = `apollo-shade-ar-preview-${Date.now()}.jpg`;
    link.href = capturedImage;
    link.click();
  };

  const getShadeOpacity = () => {
    if (config?.fabric_material?.includes('blackout')) return 0.95;
    if (config?.fabric_material?.includes('sheer')) return 0.4;
    return 0.85;
  };

  // Calculate visible shade height based on open percent
  const getVisibleShadeHeight = () => {
    const maxOpen = shadeSize.height * 0.8;
    return shadeSize.height - (shadeOpenPercent / 100) * maxOpen;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black flex flex-col"
      data-testid="ar-viewer"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/80 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <Camera className="w-5 h-5 text-white" />
          <span className="text-white font-medium">AR Preview</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Color picker toggle */}
          <Button 
            onClick={() => setShowColorPicker(!showColorPicker)} 
            variant="ghost" 
            className="text-white hover:bg-white/20"
          >
            <Palette className="w-5 h-5" />
          </Button>
          <Button onClick={onClose} variant="ghost" className="text-white hover:bg-white/20">
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Color Picker Panel */}
      {showColorPicker && (
        <motion.div 
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="bg-black/90 px-4 py-3 border-b border-white/10"
        >
          <p className="text-white/70 text-sm mb-2">Select Shade Color:</p>
          <div className="flex gap-2 flex-wrap">
            {SHADE_COLORS.map((color) => (
              <button
                key={color.id}
                onClick={() => setCurrentColor(color)}
                className={`w-10 h-10 rounded-full border-2 transition-all ${
                  currentColor?.id === color.id 
                    ? 'border-amber-400 scale-110 ring-2 ring-amber-400/50' 
                    : 'border-white/30 hover:scale-105'
                }`}
                style={{ backgroundColor: color.hex }}
                title={color.name}
              />
            ))}
          </div>
          <p className="text-amber-400 text-sm mt-2">Selected: {currentColor?.name}</p>
        </motion.div>
      )}

      {/* Camera View */}
      <div 
        ref={containerRef}
        className="flex-1 relative overflow-hidden touch-none"
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
        onTouchCancel={handleEnd}
      >
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center bg-neutral-900">
            <div className="text-center max-w-md p-8">
              <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">Camera Access Required</h3>
              <p className="text-white/60 mb-4">{error}</p>
              <Button onClick={startCamera} className="bg-white text-black hover:bg-white/90">
                Try Again
              </Button>
            </div>
          </div>
        ) : capturedImage ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <img src={capturedImage} alt="Captured AR preview" className="max-w-full max-h-full object-contain" />
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            
            {/* Shade Overlay */}
            {isStreaming && (
              <div
                className={`absolute select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                style={{
                  left: shadePosition.x,
                  top: shadePosition.y,
                  width: shadeSize.width,
                  height: shadeSize.height,
                  touchAction: 'none',
                }}
              >
                {/* Shade visualization based on product type */}
                {(productType === 'roller' || productType === 'roller-shades') && (
                  <div className="w-full h-full relative">
                    {/* Top roller bar - draggable area */}
                    <div 
                      className="h-3 bg-gray-700 rounded-t-sm cursor-grab active:cursor-grabbing"
                      onMouseDown={handleDragStart}
                      onTouchStart={handleDragStart}
                    />
                    {/* Shade fabric */}
                    <div
                      className="w-full transition-all duration-150"
                      style={{
                        backgroundColor: currentColor?.hex || '#e5e5e5',
                        opacity: getShadeOpacity(),
                        height: `calc(${(100 - shadeOpenPercent * 0.8)}% - 16px)`,
                        boxShadow: 'inset 0 0 30px rgba(0,0,0,0.15)',
                        minHeight: '20px',
                      }}
                    />
                    {/* Bottom bar - drag to open/close */}
                    <div 
                      className="h-4 bg-gray-600 rounded-b-sm cursor-ns-resize flex items-center justify-center hover:bg-gray-500 transition-colors"
                      onMouseDown={handleShadeAdjustStart}
                      onTouchStart={handleShadeAdjustStart}
                    >
                      <GripHorizontal className="w-6 h-3 text-white/70" />
                    </div>
                    {/* Open/close indicator */}
                    {shadeOpenPercent > 0 && (
                      <div className="absolute top-3 left-0 right-0 flex items-center justify-center pointer-events-none"
                        style={{ height: `${shadeOpenPercent * 0.8}%` }}
                      >
                        <span className="text-white/40 text-xs bg-black/30 px-2 py-1 rounded">
                          {Math.round(shadeOpenPercent)}% Open
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {(productType === 'drape' || productType === 'motorized-drapes') && (
                  <div 
                    className="w-full h-full flex"
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                  >
                    <div
                      className="h-full transition-all duration-150"
                      style={{
                        width: `${32 - shadeOpenPercent * 0.3}%`,
                        backgroundColor: currentColor?.hex || '#e5e5e5',
                        opacity: getShadeOpacity(),
                        boxShadow: 'inset -15px 0 30px rgba(0,0,0,0.15)'
                      }}
                    />
                    <div className="flex-1 flex items-center justify-center">
                      {shadeOpenPercent > 0 && (
                        <span className="text-white/40 text-xs bg-black/30 px-2 py-1 rounded">
                          {Math.round(shadeOpenPercent)}% Open
                        </span>
                      )}
                    </div>
                    <div
                      className="h-full transition-all duration-150"
                      style={{
                        width: `${32 - shadeOpenPercent * 0.3}%`,
                        backgroundColor: currentColor?.hex || '#e5e5e5',
                        opacity: getShadeOpacity(),
                        boxShadow: 'inset 15px 0 30px rgba(0,0,0,0.15)'
                      }}
                    />
                    {/* Drape adjust handles */}
                    <div 
                      className="absolute left-0 top-0 bottom-0 w-6 cursor-ew-resize flex items-center justify-center"
                      onMouseDown={handleShadeAdjustStart}
                      onTouchStart={handleShadeAdjustStart}
                    >
                      <div className="w-1 h-16 bg-white/30 rounded" />
                    </div>
                    <div 
                      className="absolute right-0 top-0 bottom-0 w-6 cursor-ew-resize flex items-center justify-center"
                      onMouseDown={handleShadeAdjustStart}
                      onTouchStart={handleShadeAdjustStart}
                    >
                      <div className="w-1 h-16 bg-white/30 rounded" />
                    </div>
                  </div>
                )}
                
                {(productType === 'roman' || productType === 'roman-shades') && (
                  <div 
                    className="w-full h-full relative"
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                  >
                    <div className="h-2 bg-gray-700" />
                    <div
                      className="w-full relative transition-all duration-150"
                      style={{
                        backgroundColor: currentColor?.hex || '#e5e5e5',
                        opacity: getShadeOpacity(),
                        height: `calc(${70 - shadeOpenPercent * 0.6}% - 2px)`,
                        minHeight: '20px',
                      }}
                    >
                      {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute left-0 right-0 h-1"
                          style={{
                            bottom: `${8 + i * 18}%`,
                            background: 'linear-gradient(180deg, rgba(0,0,0,0.12) 0%, transparent 100%)'
                          }}
                        />
                      ))}
                    </div>
                    {/* Roman shade folds at bottom */}
                    <div 
                      className="absolute left-0 right-0 cursor-ns-resize"
                      style={{ bottom: `${30 + shadeOpenPercent * 0.6}%` }}
                      onMouseDown={handleShadeAdjustStart}
                      onTouchStart={handleShadeAdjustStart}
                    >
                      {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className="h-3"
                          style={{
                            backgroundColor: currentColor?.hex || '#e5e5e5',
                            opacity: getShadeOpacity() + (i * 0.02),
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                          }}
                        />
                      ))}
                      <div className="flex justify-center py-1">
                        <GripHorizontal className="w-6 h-3 text-white/50" />
                      </div>
                    </div>
                    {/* Open indicator */}
                    {shadeOpenPercent > 0 && (
                      <div className="absolute top-2 left-0 right-0 flex items-center justify-center pointer-events-none"
                        style={{ height: `${shadeOpenPercent * 0.6}%` }}
                      >
                        <span className="text-white/40 text-xs bg-black/30 px-2 py-1 rounded">
                          {Math.round(shadeOpenPercent)}% Open
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Resize handle */}
                <div
                  className="absolute bottom-0 right-0 w-10 h-10 cursor-se-resize flex items-center justify-center bg-white/20 rounded-tl-lg"
                  onMouseDown={handleResizeStart}
                  onTouchStart={handleResizeStart}
                >
                  <Maximize2 className="w-5 h-5 text-white drop-shadow-lg" />
                </div>
                
                {/* Border */}
                <div className="absolute inset-0 border-2 border-white/60 border-dashed pointer-events-none rounded" />
                
                {/* Drag indicator */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-white/30 px-3 py-1 rounded-full pointer-events-none">
                  <span className="text-white text-xs font-medium flex items-center gap-1">
                    <Move className="w-3 h-3" /> Drag to move
                  </span>
                </div>
              </div>
            )}
            
            {/* Detected windows overlay */}
            {detectedWindows.map((win, idx) => {
              const pos = getWindowDisplayPosition(win);
              return (
                <div
                  key={idx}
                  className={`absolute border-2 cursor-pointer transition-all ${
                    selectedWindow === win 
                      ? 'border-green-400 bg-green-400/20' 
                      : 'border-amber-400 bg-amber-400/10 hover:bg-amber-400/20'
                  }`}
                  style={pos}
                  onClick={() => applyWindowDetection(win)}
                  title="Click to apply shade to this window"
                />
              );
            })}
          </>
        )}
        
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Shade Controls */}
      {isStreaming && !capturedImage && (
        <div className="px-4 py-2 bg-black/70 flex items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-white/70 text-sm">Shade:</span>
            <Button
              onClick={() => setShadeOpenPercent(Math.max(0, shadeOpenPercent - 20))}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 h-8 w-8 p-0"
              disabled={shadeOpenPercent <= 0}
            >
              <ChevronDown className="w-5 h-5" />
            </Button>
            <span className="text-white w-16 text-center text-sm">
              {shadeOpenPercent === 0 ? 'Closed' : `${Math.round(shadeOpenPercent)}% Open`}
            </span>
            <Button
              onClick={() => setShadeOpenPercent(Math.min(80, shadeOpenPercent + 20))}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 h-8 w-8 p-0"
              disabled={shadeOpenPercent >= 80}
            >
              <ChevronUp className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="p-4 bg-black/80 backdrop-blur-sm flex items-center justify-center gap-3">
        {capturedImage ? (
          <>
            <Button onClick={() => setCapturedImage(null)} variant="outline" className="border-white/30 text-white hover:bg-white/20">
              <RotateCcw className="w-4 h-4 mr-2" />
              Retake
            </Button>
            <Button onClick={handleDownload} className="bg-white text-black hover:bg-white/90">
              <Download className="w-4 h-4 mr-2" />
              Save Image
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={detectWindows} 
              disabled={!isStreaming || detecting}
              variant="outline" 
              className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
            >
              {detecting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4 mr-2" />
              )}
              {detecting ? 'Detecting...' : 'Auto-Detect Window'}
            </Button>
            <Button onClick={handleCapture} disabled={!isStreaming} className="bg-white text-black hover:bg-white/90 px-8">
              <Camera className="w-4 h-4 mr-2" />
              Capture
            </Button>
          </>
        )}
      </div>
      
      {/* Info bar */}
      <div className="px-4 py-2 bg-amber-600 text-center">
        <p className="text-sm text-white flex items-center justify-center gap-2">
          <Smartphone className="w-4 h-4" />
          Drag shade to position • Corner to resize • Bottom bar to open/close • Tap palette for colors
        </p>
      </div>
    </motion.div>
  );
};

export default ARViewer;
